import { BreedTypes } from "../../data/breeds";
import { Point } from "../../engine/space/bounds";
import { getAllDirections } from "../../engine/space/directions";
import { Actor, Piece } from "../actors/Actor";
import EntityManager from "../EntityManager";
import { gameData } from "../GameManager";
import { FloorTypes } from "../map/Map";
import { WordList } from "./WordDictionary";

export const piecePos = (piece: Point) => piece.x + piece.y * 1000;
export const piecePosByCoord = (x: number, y: number) => x + y * 1000;

export const isCharAvailable = (
  ch: string,
  selected: string[],
  letterBank: string[]
) => {
  const selectedCount = selected.reduce(
    (count, v) => count + (v === ch ? 1 : 0),
    0
  );
  const bankCount = letterBank.reduce(
    (count, v) => count + (v === ch ? 1 : 0),
    0
  );
  return selectedCount < bankCount;
};

export const isCharAvailablePiece = (
  ch: string,
  selected: Piece[],
  letterBank: string[]
) => {
  const selectedCount = selected.reduce(
    (count, v) => count + (v.ch === ch ? 1 : 0),
    0
  );
  const bankCount = letterBank.reduce(
    (count, v) => count + (v === ch ? 1 : 0),
    0
  );
  return selectedCount < bankCount;
};

export const removeLetters = (
  source: string[],
  removing: (string | null)[]
) => {
  const used: boolean[] = [];
  return source.filter((ch) => {
    const index = removing.findIndex(
      (letter, idx) => letter === ch && !used[idx]
    );
    if (index !== -1) {
      used[index] = true;
      return false;
    }
    return true;
  });
};

export type WordResult = {
  valid: boolean;
  word: (string | null)[];
  pieces: null | Piece[];
  seenOwner?: boolean;
  failedCross?: boolean;
  crosses?: WordResult[];
  direction: Point;
  ownersCovered?: number[];
  lettersUsed?: number;
};

export const buildPieceSet = (
  origin: Point,
  direction: Point,
  word: string,
  index: number,
  owner?: number,
  skipIndex = false
) => {
  let unique = false;
  const result = [];
  let x, y;
  for (let cIndex = 0; cIndex < word.length; cIndex++) {
    if (!skipIndex || cIndex !== index) {
      x = origin.x + (cIndex - index) * direction.x;
      y = origin.y + (cIndex - index) * direction.y;

      if (gameData.current.tileAt(x, y)?.type !== FloorTypes.EMPTY) {
        return null;
      }

      const existing = gameData.pieces.get(piecePosByCoord(x, y));
      if (existing != null) {
        if (existing.ch === word[cIndex]) {
          if (existing.owner !== owner) {
            unique = true;
          }
          //Skip this letter since it's already on the board from another source
          continue;
        } else {
          //Stop if we hit an unexpected word & lets try it out
          break;
        }
      } else {
        unique = true;
      }
      //TODO: This is a really hairy use of pieces and could cause bugs
      const piece = new Piece(-1, BreedTypes.letterPlacement, x, y, word);
      piece.ch = word[cIndex];
      result.push(piece);
    }
  }
  return [result, unique] as [Piece[], boolean];
};

export const checkCorners = (direction: Point, center: Point, word: string) => {
  const fits = [];
  const originA = { x: center.x + direction.y, y: center.y + direction.x };
  const originB = {
    x: center.x - direction.y,
    y: center.y - direction.x,
  };

  for (let index = 0; index < word.length; index++) {
    const resultA = buildPieceSet(originA, direction, word, index);
    const resultB = buildPieceSet(originB, direction, word, index);
    if (resultA) {
      const [pieces, unique] = resultA;
      if (unique) {
        const wordInfo = getWordInfo(
          direction,
          {
            x: originA.x - index * direction.x,
            y: originA.y - index * direction.y,
          },
          pieces
        );
        fits.push(wordInfo);
      }
    }

    if (resultB) {
      const [pieces, unique] = resultB;
      if (unique) {
        const wordInfo = getWordInfo(
          direction,
          {
            x: originB.x - index * direction.x,
            y: originB.y - index * direction.y,
          },
          pieces
        );
        fits.push(wordInfo);
      }
    }
  }
  return fits;
};

export const fitAndValidateWord = (
  direction: Point,
  origin: Piece,
  word: string,
  owner: number
) => {
  const indexes = [];
  for (let cIndex = 0; cIndex < word.length; cIndex++) {
    if (word[cIndex] === origin.ch) {
      indexes.push(cIndex);
    }
  }

  const results: WordResult[] = indexes.map((index) => {
    const result = buildPieceSet(origin, direction, word, index, owner, true);
    if (result) {
      const [pieces, unique] = result;
      if (unique) {
        const wordInfo = getWordInfo(
          direction,
          {
            x: origin.x - index * direction.x,
            y: origin.y - index * direction.y,
          },
          pieces
        );
        return wordInfo;
      }
    }
    return { valid: false, direction, word: [], pieces: null };
  });
  return results;
};

export const getWordInfo = (
  direction: Point,
  origin: Point,
  letters: Piece[],
  ownerNeeded?: number,
  checkCrosses = true
): WordResult => {
  let failedCross = false;
  const crosses: WordResult[] = [];
  const ownersCovered = [];
  let seenOwner = ownerNeeded == null;
  const pieceList = [];
  const word = [];
  let { x, y } = origin;
  let index = 0;

  let prior = true;
  while (prior) {
    const pX = x - direction.x;
    const pY = y - direction.y;
    prior = gameData.pieces.get(piecePosByCoord(pX, pY)) != null;
    if (prior) {
      x = pX;
      y = pY;
    }
  }

  let pieceFromLetters = false;
  let failsafe = 0;
  while (failsafe < 1000) {
    failsafe += 1;
    pieceFromLetters = false;
    let piece = gameData.pieces.get(piecePosByCoord(x, y));
    if (piece) {
      if (!seenOwner) {
        seenOwner = piece.owner === ownerNeeded;
      }
      ownersCovered.push(piece.owner);
    }
    if (piece == null && index < letters.length) {
      pieceFromLetters = true;
      piece = letters[index];
      index += 1;
      if (piece.x != x || piece.y != y) {
        console.log(x, piece.x, piece.y, y);
        console.error("Letter was chosen at incorrect spot");
      }
    }
    if (piece) {
      if (pieceFromLetters && checkCrosses) {
        const cross = getWordInfo(
          { x: direction.y, y: direction.x },
          piece,
          pieceFromLetters ? [piece] : [],
          ownerNeeded,
          false
        );
        if (cross.word.length > 1) {
          failedCross = failedCross || !cross.valid;
          if (!seenOwner) {
            seenOwner = !!cross.seenOwner;
          }

          crosses.push(cross);
        }
      }
      pieceList.push(piece);
      word.push(piece.ch);
    } else {
      break;
    }
    x = x + direction.x;
    y = y + direction.y;
  }

  if (word.length === 1) {
    return {
      valid: true,
      word: word,
      pieces: null,
      seenOwner: false,
      crosses,
      failedCross,
      direction,
      ownersCovered,
      lettersUsed: index,
    };
  } else if (WordList.lookup(word.join("").toLocaleLowerCase())) {
    return {
      valid: true,
      word: word,
      pieces: pieceList,
      seenOwner,
      crosses,
      failedCross,
      direction,
      ownersCovered,
      lettersUsed: index,
    };
  } else {
    return {
      valid: false,
      word: word,
      pieces: pieceList,
      failedCross,
      crosses,
      seenOwner,
      direction,
      ownersCovered,
    };
  }
};

export const swapWord = (actor: Actor, letters: string | string[]) => {
  actor.pieces.forEach((next) => {
    gameData.pieces.remove(piecePos(next));
  });
  let x = actor.x;
  let y = actor.y;
  actor.pieces.clear();
  for (let i = 0; i < letters.length; i++) {
    const ch = letters[i];
    const piece = EntityManager.createPiece(
      actor.breed.pieceBreed || BreedTypes.letterPlacement,
      x,
      y,
      actor.id,
      ch
    );
    actor.pieces.add(piece.id, piece);
    x += actor.pieceDirection?.x || 0;
    y += actor.pieceDirection?.y || 0;
  }
};

export const countAdjacentOthers = (piece: Piece) => {
  let pieceAt;
  let count = 0;
  const directions = getAllDirections(piece);
  directions.forEach((dir) => {
    pieceAt = gameData.pieces.get(piecePosByCoord(dir.x, dir.y));
    if (pieceAt && pieceAt.owner !== piece.owner) {
      count += 1;
    }
  });
  return count;
};

export const countAdjacent = (point: Point) => {
  let pieceAt;
  let count = 0;
  const directions = getAllDirections(point);
  directions.forEach((dir) => {
    pieceAt = gameData.pieces.get(piecePosByCoord(dir.x, dir.y));
    if (pieceAt) {
      count += 1;
    }
  });
  return count;
};
